/**
 * Themavoorkeur: systeem volgen, altijd licht, of altijd donker.
 *
 * Bewust een module en geen React-context, om drie redenen in volgorde van gewicht:
 *
 * 1. De keuze moet ook gelden op de publieke spelerspagina, die geen inlog heeft en dus
 *    buiten AuthProvider valt. Een module heeft geen provider en kan daarom per definitie
 *    niet op de verkeerde plek in de boom belanden - er ís geen boom. Een context zou
 *    bóven AuthProvider in App.tsx moeten staan, en dat is een afspraak die iemand over
 *    een jaar per ongeluk omdraait.
 * 2. Het spiegelt i18n.ts: geëxporteerde constanten, een uniontype dat daaruit volgt, en
 *    één abonnee die een DOM-eigenschap gelijkhoudt (`documentElement.lang` daar,
 *    `data-thema` hier).
 * 3. Vrijwel niets in React hoeft de waarde te weten - de CSS doet het werk via het
 *    attribuut op <html>. Alleen de keuzeknop en de native statusbalk lezen mee. Een
 *    context zou bij elke systeemwissel de hele boom hertekenen om nul pixels te
 *    veranderen.
 *
 * LET OP: public/index.html bevat een kopie van de leeslogica hieronder, die vóór de
 * eerste verf draait zodat een koude start in donkere modus niet wit opflitst. Die twee
 * moeten hetzelfde zeggen; thema.test.ts bewaakt dat.
 */
import { useCallback, useSyncExternalStore } from 'react';

export const THEMA_OPSLAGSLEUTEL = 'hockey-app-theme';
export const THEMA_ATTRIBUUT = 'data-thema';
export const THEMA_KEUZES = ['systeem', 'licht', 'donker'] as const;
/**
 * Licht en niet 'systeem'. Wie niets kiest krijgt het uiterlijk waarop de app is
 * ontworpen; donker is een bewuste keuze, geen gevolg van een instelling elders op het
 * toestel. 'systeem' blijft wel gewoon te kiezen - het is alleen niet het vertrekpunt.
 */
export const STANDAARD_THEMA: ThemaKeuze = 'licht';

/** Wat de gebruiker kiest. */
export type ThemaKeuze = (typeof THEMA_KEUZES)[number];
/** Wat daar uiteindelijk uitkomt; 'systeem' bestaat op dit niveau niet meer. */
export type ToegepastThema = 'licht' | 'donker';

/** De kleur van de browserchrome per thema. Moet gelijk blijven aan public/index.html. */
export const THEMA_META_KLEUR: Record<ToegepastThema, string> = {
  licht: '#8BD0F4',
  donker: '#296C8E',
};

const DONKER_QUERY = '(prefers-color-scheme: dark)';

/**
 * Zelfde vorm als isCoarsePointer() in utils/pointer.ts: jsdom kent matchMedia niet en
 * setupTests.ts mockt het niet, dus de afwezigheid ervan telt als licht. Dat is ook de
 * stand waarin de bestaande tests draaien en het houdt deze functie bruikbaar zonder dat
 * elke test een mock hoeft neer te zetten.
 */
export function systeemIsDonker(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DONKER_QUERY).matches
  );
}

function isThemaKeuze(waarde: unknown): waarde is ThemaKeuze {
  return THEMA_KEUZES.includes(waarde as ThemaKeuze);
}

/**
 * Leest de opgeslagen keuze. Onbekende of beschadigde waarden vallen terug op de
 * standaard in plaats van een lege pagina op te leveren; localStorage kan bovendien
 * gooien in de privémodus van Safari, vandaar de try.
 */
export function leesThemaKeuze(): ThemaKeuze {
  try {
    const opgeslagen = window.localStorage.getItem(THEMA_OPSLAGSLEUTEL);
    return isThemaKeuze(opgeslagen) ? opgeslagen : STANDAARD_THEMA;
  } catch {
    return STANDAARD_THEMA;
  }
}

export function bepaalThema(keuze: ThemaKeuze): ToegepastThema {
  if (keuze === 'systeem') return systeemIsDonker() ? 'donker' : 'licht';
  return keuze;
}

let huidigeKeuze: ThemaKeuze = STANDAARD_THEMA;
let huidigThema: ToegepastThema = 'licht';
const abonnees = new Set<() => void>();

function pasToe(): void {
  const thema = bepaalThema(huidigeKeuze);
  huidigThema = thema;

  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute(THEMA_ATTRIBUUT, thema);
    // Rechtstreeks op het element en niet alleen via CSS: hiermee kiest de browser ook de
    // kleur van het doek, van de schuifbalken en van de overscroll-rand van de webview.
    document.documentElement.style.colorScheme = thema === 'donker' ? 'dark' : 'light';

    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', THEMA_META_KLEUR[thema]);
  }

  abonnees.forEach((abonnee) => abonnee());
}

export function huidigeThemaKeuze(): ThemaKeuze {
  return huidigeKeuze;
}

export function toegepastThema(): ToegepastThema {
  return huidigThema;
}

export function zetThemaKeuze(keuze: ThemaKeuze): void {
  huidigeKeuze = keuze;
  try {
    window.localStorage.setItem(THEMA_OPSLAGSLEUTEL, keuze);
  } catch {
    // Privémodus: de keuze geldt dan alleen voor deze sessie. Beter dan niets doen.
  }
  pasToe();
}

export function abonneerOpThema(abonnee: () => void): () => void {
  abonnees.add(abonnee);
  return () => {
    abonnees.delete(abonnee);
  };
}

let geinitialiseerd = false;

/**
 * Zet de opgeslagen keuze om in het attribuut en houdt hem daarna bij.
 *
 * Wordt vanuit index.tsx aangeroepen, naast de i18n-import. Het voorpaint-script in
 * public/index.html heeft het attribuut dan al gezet; deze aanroep neemt het over en
 * voegt de luisteraars toe.
 */
export function initThema(): void {
  if (geinitialiseerd) return;
  geinitialiseerd = true;

  huidigeKeuze = leesThemaKeuze();
  pasToe();

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

  // Onvoorwaardelijk aan, ook bij 'licht' en 'donker': daar levert het herberekenen
  // dezelfde uitkomst en verandert er niets. Zo hoeft er geen luisteraar aan- en
  // afgekoppeld te worden bij elke keuzewissel, wat een bron van vergeten opruimen is.
  const query = window.matchMedia(DONKER_QUERY);
  // addEventListener en niet addListener: die laatste is verouderd, maar iOS 15 (het
  // implementatiedoel van de app) ondersteunt de moderne vorm al.
  query.addEventListener('change', pasToe);

  // Twee tabbladen naast elkaar horen niet uit elkaar te lopen. `storage` vuurt alleen in
  // de ándere tabbladen, dus dit botst niet met zetThemaKeuze hierboven.
  window.addEventListener('storage', (gebeurtenis) => {
    if (gebeurtenis.key !== THEMA_OPSLAGSLEUTEL) return;
    huidigeKeuze = leesThemaKeuze();
    pasToe();
  });
}

export function useThema(): {
  keuze: ThemaKeuze;
  toegepast: ToegepastThema;
  kies: (keuze: ThemaKeuze) => void;
} {
  const keuze = useSyncExternalStore(abonneerOpThema, huidigeThemaKeuze, () => STANDAARD_THEMA);
  const toegepast = useSyncExternalStore(abonneerOpThema, toegepastThema, () => 'licht' as const);
  const kies = useCallback((nieuw: ThemaKeuze) => zetThemaKeuze(nieuw), []);

  return { keuze, toegepast, kies };
}
